import { Button } from "@package/subframe/components/Button";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherEye, FeatherEyeOff, FeatherWifiOff } from "@subframe/core";
import axios from "axios";
import { useState } from "react";
import { toast } from "sonner";

import type { ManageableNurseryDto } from "@/generated/model/manageableNurseryDto";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { generateDeviceUuid, saveDeviceLink } from "@/lib/device/device-link";

import {
  fetchLinkedUsers,
  fetchManageableNurseries,
  loginForDeviceAuth,
  setupDevice,
} from "./device-setup-api";

const INVALID_CREDENTIALS = "Incorrect email or password. Please try again.";
const OFFLINE_MESSAGE =
  "No internet connection. Device setup requires a network connection.";
const GENERIC_ERROR = "Something went wrong. Please try again.";

interface SetupScreenProps {
  /** Called once the device is linked and the user list is cached. */
  onLinked: () => void;
}

/**
 * Shared-device setup (NAU-226). Step 1: a manager authenticates with
 * email/password (always, regardless of PIN login). Step 2: they pick one of
 * the nurseries they manage; on link we cache the nursery's users locally and
 * hand off to the PIN-login shell.
 */
export function SetupScreen({ onLinked }: SetupScreenProps) {
  const isOnline = useOnlineStatus();

  const [step, setStep] = useState<"auth" | "select">("auth");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [nurseries, setNurseries] = useState<ManageableNurseryDto[]>([]);
  const [selectedNurseryId, setSelectedNurseryId] = useState<string>("");
  const [deviceUuid] = useState<string>(() => generateDeviceUuid());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAuthenticate() {
    if (!isOnline) {
      setError(OFFLINE_MESSAGE);
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const token = await loginForDeviceAuth(email.trim(), password);
      const { nurseries: manageable } = await fetchManageableNurseries(token);
      setIdToken(token);
      setNurseries(manageable);
      setSelectedNurseryId(manageable[0]?.id ?? "");
      setStep("select");
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError(INVALID_CREDENTIALS);
      } else {
        setError(GENERIC_ERROR);
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleLink() {
    if (!isOnline) {
      setError(OFFLINE_MESSAGE);
      return;
    }
    if (!idToken || !selectedNurseryId) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const result = await setupDevice(idToken, deviceUuid, selectedNurseryId);
      // The secret is returned once, here — it has to reach IndexedDB or the
      // device can never refresh its roster or accept a PIN login.
      const linkedUsers = await fetchLinkedUsers(
        deviceUuid,
        result.deviceSecret,
      );
      await saveDeviceLink({
        deviceUuid,
        deviceSecret: result.deviceSecret,
        nurseryId: result.nurseryId,
        nurseryName: result.nurseryName,
        pinLoginEnabled: result.pinLoginEnabled,
        cachedUsers: linkedUsers.users,
      });
      toast.success(`Device linked to ${result.nurseryName}`);
      onLinked();
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center bg-white px-6 py-12">
      <div className="flex w-full max-w-[384px] flex-col items-start gap-6">
        <img
          className="h-8 flex-none object-cover"
          src="/images/logo.svg"
          alt="Logo"
        />

        <div className="flex flex-col items-start gap-1">
          <span className="text-heading-1 font-heading-1 text-neutral-900">
            Set up this device
          </span>
          <span className="text-body font-body text-subtext-color">
            {step === "auth"
              ? "Sign in as a manager to link this device to your nursery."
              : "Choose the nursery this device belongs to."}
          </span>
        </div>

        {!isOnline && (
          <div className="flex w-full items-center gap-2 rounded-md bg-error-50 px-4 py-3">
            <FeatherWifiOff className="text-error-700" />
            <span className="text-body font-body text-error-700">
              {OFFLINE_MESSAGE}
            </span>
          </div>
        )}

        {step === "auth" && (
          <form
            className="flex w-full flex-col items-start gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              void handleAuthenticate();
            }}
          >
            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Email"
              error={!!error}
              helpText={error ?? undefined}
            >
              <TextField.Input
                type="email"
                placeholder="manager@nursery.com"
                value={email}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                disabled={isSubmitting}
                onChange={(e) => setEmail(e.target.value)}
              />
            </TextField>

            <TextField
              className="h-auto w-full flex-none"
              size="large"
              label="Password"
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="cursor-pointer border-none bg-transparent p-0"
                >
                  {showPassword ? <FeatherEye /> : <FeatherEyeOff />}
                </button>
              }
            >
              <TextField.Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter password"
                value={password}
                autoComplete="current-password"
                disabled={isSubmitting}
                onChange={(e) => setPassword(e.target.value)}
              />
            </TextField>

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={isSubmitting || !isOnline || !email || !password}
              loading={isSubmitting}
            >
              Continue
            </Button>
          </form>
        )}

        {step === "select" && (
          <div className="flex w-full flex-col items-start gap-6">
            {nurseries.length === 0 ? (
              <span className="text-body font-body text-subtext-color">
                You don&apos;t manage any nurseries. Ask an administrator to
                grant you access.
              </span>
            ) : (
              <>
                <Select
                  className="h-auto w-full flex-none"
                  label="Nursery"
                  value={selectedNurseryId}
                  onValueChange={setSelectedNurseryId}
                  disabled={isSubmitting}
                >
                  {nurseries.map((nursery) => (
                    <Select.Item key={nursery.id} value={nursery.id}>
                      {nursery.name}
                    </Select.Item>
                  ))}
                </Select>

                {error && (
                  <span className="text-body font-body text-error-700">
                    {error}
                  </span>
                )}

                <Button
                  className="h-12 w-full flex-none"
                  variant="brand-primary"
                  size="large"
                  type="button"
                  disabled={isSubmitting || !isOnline || !selectedNurseryId}
                  loading={isSubmitting}
                  onClick={() => void handleLink()}
                >
                  Link device
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
